import { useEffect, useRef, useState } from 'react'
import { Settings2 } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'

// Extracted from VoiceChannelControls so the DM call surface
// (TelegramCallOverlay) can drop the same picker into its control bar
// — voice channels and DM calls share the same publish/subscribe
// preset stored in voiceStore.

const QUALITY_OPTIONS: Array<{ value: 'auto' | 'low' | 'hd' | 'fhd'; label: string; hint: string }> = [
  { value: 'fhd', label: 'Full HD 1080p', hint: 'Max quality, ~8 Mbps uplink' },
  { value: 'hd', label: 'HD 720p', hint: 'Balanced, ~4 Mbps uplink' },
  { value: 'low', label: 'Low 180p', hint: 'Bandwidth-saver, ~250 kbps uplink' },
]

const INCOMING_OPTIONS: Array<{ value: 'auto' | 'medium' | 'low'; label: string; hint: string }> = [
  { value: 'auto', label: 'Auto', hint: 'Highest layer my downlink can carry' },
  { value: 'medium', label: 'Medium 540p', hint: 'Cap at ~800 kbps per peer' },
  { value: 'low', label: 'Low 180p', hint: 'Cap at ~150 kbps per peer' },
]

interface Props {
  /** Size of the trigger button. md = voice room (10x10), sm = DM call (8x8). */
  size?: 'sm' | 'md'
  /** Side from which the dropdown opens. Defaults to right (good for end of toolbar). */
  align?: 'left' | 'right'
}

export default function VideoQualityPicker({ size = 'md', align = 'right' }: Props) {
  const videoQuality = useVoiceStore((s) => s.videoQuality)
  const setVideoQuality = useVoiceStore((s) => s.setVideoQuality)
  const incomingQuality = useVoiceStore((s) => s.incomingQuality)
  const setIncomingQuality = useVoiceStore((s) => s.setIncomingQuality)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const btnCls = size === 'sm'
    ? 'w-8 h-8 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150'
    : 'w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150'
  const iconSize = size === 'sm' ? 14 : 20
  const alignCls = align === 'right' ? 'right-0' : 'left-0'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title={`Send: ${videoQuality} · Receive: ${incomingQuality}. Click to change.`}
        className={btnCls}
      >
        <Settings2 size={iconSize} />
      </button>
      {open && (
        <div className={`absolute bottom-full mb-2 ${alignCls} w-64 bg-card rounded-lg ring-1 ring-border shadow-lg overflow-hidden z-30`}>
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
            My video — send
          </div>
          {QUALITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setVideoQuality(opt.value) }}
              className={`w-full text-left px-3 py-2 hover:bg-accent transition flex items-start gap-2 ${
                videoQuality === opt.value ? 'bg-primary/10' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${videoQuality === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight">{opt.hint}</div>
              </div>
              {videoQuality === opt.value && (
                <span className="text-primary text-sm leading-none mt-0.5">✓</span>
              )}
            </button>
          ))}
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-y border-border">
            Receive — downlink cap
          </div>
          {INCOMING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setIncomingQuality(opt.value) }}
              className={`w-full text-left px-3 py-2 hover:bg-accent transition flex items-start gap-2 ${
                incomingQuality === opt.value ? 'bg-primary/10' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${incomingQuality === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </div>
                <div className="text-[11px] text-muted-foreground leading-tight">{opt.hint}</div>
              </div>
              {incomingQuality === opt.value && (
                <span className="text-primary text-sm leading-none mt-0.5">✓</span>
              )}
            </button>
          ))}
          <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
            Send change blinks the camera ~200ms. Receive applies instantly
            to cameras; screen shares always stream at full quality.
          </div>
        </div>
      )}
    </div>
  )
}
